// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import RouteBuilder from '@Services/RouteBuilder';
import client, { queryClient } from '@Client';
import useOneQuery from '@Hooks/useOneQuery';
import { RouteType } from '@Types';
import useCasePermissions from '@Hooks/useCasePermissions';
import { computeEventList } from '@Organisms/Task/eventList';
import { actionList } from '@Organisms/Task/actionList';
import Details from '@Molecules/Details/Details';
import { extractData } from '@Organisms/Task/extractData';
import { useCurrentUser } from '@Queries/userQueries';
import { THLayout } from '@Molecules/THLayout';
import { useGenericCountQuery, useGenericQuery } from '@Queries/genericQueries';
import { computeEventList as computeTaskLogEventList } from '@Organisms/TaskLog/eventList';
import { PinType, PinTypes } from '@Molecules/Pin';
import useListQuery from '@Hooks/useListQuery';
import usePagination from '@Hooks/usePagination';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { THForm } from '@Molecules/THForm/THForm';
import moment from 'moment';
import { Form } from 'antd';
import { StatusContext } from '@Context/StatusProvider';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { EntityHeader } from '@Atoms/EntityHeader/EntityHeader';
import { RequireActionButton } from '@Organisms/Task/RequireActionButton/RequireActionButton';
import { useListAllFields } from '@Queries/describeQueries';
import { computeEventList as computeCaseEventList } from '@Organisms/Case/eventList';
import { useDrawer } from '@Hooks/useDrawer';
import { computeGetSelector } from '@Organisms/Task/selector';
import { SharedOrgOutput, AuditOutput, OrgPermissionsEnum, TaskOutput, TaskStatus, UserOutput } from 'thehive-sdk';
import { computeGetSelector as computeCaseGetSelector } from '@Organisms/Case/selector';

import './TaskDetails.scss';

const actionIsTask = (audit: AuditOutput): boolean => audit.base?.details?.objectType === 'Task';

const TaskDetails = (): ReactElement => {
    const { caseId, taskId } = useParams<{ caseId: string; taskId: string }>();

    const i = useLocalMessage();
    const { hasCortex } = useContext(StatusContext);

    const history = useHistory();
    const [form] = Form.useForm();
    const { data: currentUser } = useCurrentUser();
    const { organisation = '', organisations = [] } = currentUser || {};
    const links = useMemo(
        () => organisations.find(({ organisation: orgName }) => orgName === organisation)?.links || [],
        [organisation, organisations],
    );

    const paginationManager = usePagination(10);
    const { itemsPerPage, page } = paginationManager;

    const { data: taskReports = [], refetch: reportsRefetch } = useListQuery({
        queryParams: {
            selector: {
                name: 'get-task-reports',
                head: [
                    {
                        name: 'getTask',
                        idOrName: taskId,
                    },
                    {
                        name: 'actions',
                    },
                ],
            },
            sort: ['-startDate'],
        },
        controller: client.TaskController,
    });

    const { data: describeData } = useListAllFields();

    const [editMode, setEditMode] = useState(false);
    const { visible: visibleDrawer, setVisible: setVisibleDrawer } = useDrawer({});
    const [tmpRefetch, setTmpRefetch] = useState<string[]>([]);

    const caseEventList = useMemo(
        () =>
            computeCaseEventList({
                controller: client.CaseController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: () => {
                    return;
                },
            }),
        [i],
    );

    const {
        data: taskDetails,
        error,
        refetch,
    } = useOneQuery<TaskOutput>({
        selector: computeGetSelector({ taskId }),
        controller: client.TaskController,
    });

    const { data: sharedOrgList = [], refetch: refetchSharedOrgList } = useGenericQuery<SharedOrgOutput[]>({
        fetchCallback: () => caseEventList.shares.onList(caseId),
        key: ['sharedOrgList', caseId],
    });

    const { data: tasksList = [] } = useListQuery<TaskOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getCase', idOrName: caseId }, { name: 'tasks' }],
                name: `get-case-tasks-${caseId}`,
                initialFilters: [
                    {
                        field: 'status',
                        operator: 'not',
                        values: [TaskStatus.CANCEL],
                        type: 'string',
                        describeAttribute: describeData?.task.attributes.find(({ name }) => name === 'status') as any,
                    },
                ],
                sort: ['-startDate'],
            },
        },
        controller: client.TaskController,
    });

    const { data: assignableUsers = [], refetch: refetchAssignableUsers } = useListQuery<UserOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getTask', idOrName: taskId }, { name: 'assignableUsers' }],
                name: `get-task-${taskId}-assignable-users`,
                initialFilters: [
                    {
                        field: 'locked',
                        operator: 'any',
                        values: [false],
                        type: 'boolean',
                        describeAttribute: describeData?.user.attributes.find(({ name }) => name === 'locked') as any,
                    },
                ],
                sort: ['+name'],
            },
        },
        controller: client.UserController,
        manual: true,
        fetchOnStart: true,
    });

    const { data: taskLogList = [], refetch: taskLogListRefetch } = useListQuery({
        queryParams: {
            selector: {
                head: [{ name: 'getTask', idOrName: taskId }, { name: 'logs' }],
                extraData: ['actionCount'],
                name: `get-task-logs-${taskId}`,
            },
            paginationOptions: { page, itemsPerPage },
            sort: ['-date'],
        },
        controller: client.TaskLogController,
    });

    const { data: taskLogCount = 0, refetch: taskLogCountRefetch } = useGenericCountQuery({
        queryParams: {
            selector: {
                head: [{ name: 'getTask', idOrName: taskId }, { name: 'logs' }],
                name: `get-task-logs-${taskId}`,
            },
        },
        controller: client.TaskLogController,
    });

    const listTaskLogsIds: string[] = useMemo(() => taskLogList.map(({ _id }) => _id), [taskLogList]);

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.TASK },
        (data) => {
            const canRefetch = data.some((action) => action.base.objectId === taskId);

            if (canRefetch) {
                if (!visibleDrawer && !editMode) {
                    refetch();
                } else {
                    setTmpRefetch([...tmpRefetch, 'refetch']);
                }
            }
        },
        [visibleDrawer, editMode],
    );

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.LOG },
        (data: any[]) => {
            const canRefetch = data.some((action) => {
                return (
                    (action.base?.action === 'create' && action.base?.object?.task?._id === taskId) ||
                    action.base?.action === 'delete' ||
                    listTaskLogsIds.includes(action?.base?.objectId || '')
                );
            });

            if (canRefetch) {
                if (!visibleDrawer && !editMode) {
                    taskLogListRefetch();
                    taskLogCountRefetch();
                    (TaskStatus.WAITING === taskDetails?.status || taskDetails?.mandatory) && refetch();
                } else {
                    setTmpRefetch([
                        ...tmpRefetch,
                        'taskLogListRefetch',
                        'taskLogCountRefetch',
                        ...(TaskStatus.WAITING === taskDetails?.status || taskDetails?.mandatory ? ['refetch'] : []),
                    ]);
                }
            }
        },
        [visibleDrawer, editMode, listTaskLogsIds, taskDetails],
    );

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.CASE },
        (data) => {
            const canRefetchShare = data.some(({ base }) => {
                const { details } = base || {};
                return details?.share || details?.taskRule || details?.observableRule;
            });
            if (canRefetchShare) {
                if (!visibleDrawer && !editMode) {
                    refetchSharedOrgList();
                } else {
                    setTmpRefetch([...tmpRefetch, 'refetchSharedOrgList']);
                }
            }
        },
        [visibleDrawer, editMode],
    );

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.ACTION },
        (audits: AuditOutput[]): void => {
            if (audits.some(actionIsTask)) {
                if (!visibleDrawer && !editMode) {
                    reportsRefetch();
                } else {
                    setTmpRefetch([...tmpRefetch, 'reportsRefetch']);
                }
            }
            if (audits.some(({ base }) => base.details.objectType === 'Log')) {
                if (!visibleDrawer && !editMode) {
                    taskLogListRefetch();
                    taskLogCountRefetch();
                } else {
                    setTmpRefetch([...tmpRefetch, 'taskLogListRefetch', 'taskLogCountRefetch']);
                }
            }
        },
        [visibleDrawer, editMode],
    );

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.TaskController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (_, message) => {
                    refetch();
                    if (message) {
                        openMessage('success', message);
                    }
                    queryClient.refetchQueries(['sharings', taskId]);
                },
            }),
        [refetch, i, taskId],
    );

    const taskLogEventList = useMemo(
        () =>
            computeTaskLogEventList({
                controller: client.TaskLogController,
                locale: i,
                onSuccess: (count, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    if (count) {
                        taskLogCountRefetch();
                    }
                    taskLogListRefetch();
                },
                onError: handleNotificationError,
            }),
        [taskLogCountRefetch, taskLogListRefetch, i],
    );

    const { data: shareList = [] } = useGenericQuery({
        fetchCallback: () =>
            eventList.shares.onList(taskId, [
                {
                    field: 'organisationName',
                    type: 'string',
                    values: organisation,
                    operator: 'not',
                    describeAttribute: '' as any,
                },
            ]),
        key: ['sharings', taskId],
    });

    const taskActionList = useMemo(
        () =>
            taskDetails &&
            actionList(
                i,
                history,
                caseId,
                undefined,
                taskDetails?.extraData?.case?.userPermissions,
            )([taskDetails], () => refetch(), 'details'),

        [i, refetch, history, taskDetails, caseId],
    );

    const hasPermissions = useCasePermissions({ caseId, permissionsNeeded: [OrgPermissionsEnum.MANAGE_TASK] });
    const hasSharePermissions = useCasePermissions({ caseId, permissionsNeeded: [OrgPermissionsEnum.MANAGE_SHARE] });

    useEffect(() => {
        if (error) {
            openMessage('error', i('notification.task.doesnotExist'));
            history.push(RouteBuilder.compute('cases.tasks', { caseId }));
        }
    }, [caseId, error, history, i]);

    const { status, extraData, title = '', mandatory } = taskDetails || {};
    const { actionRequired = false } = extraData || {};
    const url = RouteBuilder.compute('cases.task', { caseId, taskId });
    const pinData: PinType = { caseId, type: PinTypes.TASK, id: taskId, title, url };

    const forbiddenFields = useMemo(() => {
        const fields = [];

        if (!links.length) {
            fields.push('#computeShares');
        }
        if (status === TaskStatus.COMPLETED) {
            fields.push('dueDate');
        } else {
            fields.push('endDate');
        }

        if (mandatory) {
            fields.push('status');
        } else {
            fields.push('#statusMandatory');
        }

        return fields;
    }, [status, links, mandatory]);

    const sharedOrgFiltered = useMemo(
        () => sharedOrgList.filter(({ organisationName }) => currentUser?.organisation !== organisationName),
        [currentUser?.organisation, sharedOrgList],
    );

    const alertMessage: React.ReactNode | null = useMemo(() => {
        const children = [];
        if (actionRequired) {
            children.push(i('task.actionRequired.info.description'));
        }

        if (mandatory && taskLogCount <= 0) {
            children.push(i('task.mandatory.info.description'));
        }

        return children.length ? (
            <ul className="task-details__alert">
                {children.map((txt) => (
                    <li className="task-details__alert-item" key={txt}>
                        {txt}
                    </li>
                ))}
            </ul>
        ) : null;
    }, [actionRequired, mandatory, taskLogCount, i]);

    useEffect(() => {
        if (taskDetails) {
            form.setFieldsValue(taskDetails);
            form.resetFields();
        }
    }, [taskDetails, form]);

    useEffect(() => {
        if (!visibleDrawer && !editMode && tmpRefetch.length) {
            tmpRefetch.includes('refetch') && refetch();
            tmpRefetch.includes('reportsRefetch') && reportsRefetch();
            tmpRefetch.includes('taskLogListRefetch') && taskLogListRefetch();
            tmpRefetch.includes('taskLogCountRefetch') && taskLogCountRefetch();
            setTmpRefetch([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleDrawer, editMode, tmpRefetch]);

    return (
        <THLayout>
            <THLayout.Content>
                <THForm<TaskOutput & { dueDate: moment.Moment }>
                    form={form}
                    editable
                    initialValues={taskDetails}
                    //eslint-disable-next-line
                    onFinish={({ _createdAt, _updatedAt, ...data }) => {
                        eventList.onEdit(data, taskId).then(() => {
                            if ('status' in data) {
                                queryClient.refetchQueries([computeCaseGetSelector({ caseId }).name]);
                            }
                        });
                        setEditMode(false);
                    }}
                    submitTouchedValues
                    onValuesChange={() => {
                        setEditMode(true);
                    }}
                >
                    <THForm.Layout
                        header={
                            <EntityHeader
                                actionList={taskActionList}
                                onBackClick={() => history.push(RouteBuilder.compute('cases.tasks', { caseId }))}
                                extraAction={
                                    hasPermissions &&
                                    organisation && (
                                        <RequireActionButton
                                            task={taskDetails}
                                            actionRequired={actionRequired}
                                            eventList={eventList}
                                            organisationName={organisation}
                                            taskId={taskId}
                                            label={{
                                                requireAction: i('task.actionRequired.label'),
                                                cancelAction: i('task.actionRequired.mark-as-done'),
                                            }}
                                        />
                                    )
                                }
                                pinData={pinData}
                                title={i('task.details.header.title')}
                                alert={alertMessage}
                            />
                        }
                        footer={
                            <FormSubmitButton
                                warning
                                onCancel={() => {
                                    form.resetFields();
                                    setEditMode(false);
                                }}
                                disabled={false}
                                footerText={i('general.confirm')}
                                hidden={!editMode}
                            />
                        }
                    >
                        <Details
                            data={{
                                ...taskDetails,
                                '#statusMandatory': { valid: taskLogCount > 0 },
                                '#computeShares': { shareList, setVisible: setVisibleDrawer },
                                '#computeResponderReportList': taskReports,
                                '#computeLogs': {
                                    list: taskLogList,
                                    count: taskLogCount,
                                    paginationManager,
                                    setVisible: setVisibleDrawer,
                                },
                            }}
                            className="content"
                            extract={extractData({
                                data: taskDetails || ({} as TaskOutput),
                                eventList,
                                taskLogEventList,
                                locale: i,
                                hasPermissions,
                                hasSharePermissions,
                                shareList,
                                sharedOrgList: sharedOrgFiltered,
                                assignableUsers,
                                refetchAssignableUsers,
                                tasksList,
                                organisation,
                                caseId,
                                hasCortex,
                                formEditMode: editMode,
                            })}
                            forbidden={forbiddenFields}
                        />
                    </THForm.Layout>
                </THForm>
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    path: RouteBuilder.compute('cases.task'),
    content: <TaskDetails />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.task-details' },
};
